<template lang="pug">
.c-sticker-stats
  .c-sticker-stats__card(v-for="card in cards" :key="card.label")
    .c-sticker-stats__icon(:class="card.tone")
      fai(type="fal" :icon="card.icon")
    .c-sticker-stats__body
      .c-sticker-stats__label {{ card.label }}
      .c-sticker-stats__value {{ card.value }}
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import dayjs from 'dayjs';
import globalShared from '@/shared';
import { findOrganizationTotalRevenue } from '@/graphs/gql';

export default defineComponent({
  name: 'StickerStatsCards',
  props: {
    stickers: { type: Array as PropType<any[]>, default: () => [] },
    orgId: { type: String, default: '' },
  },
  setup(props) {
    const { makeGraphRequest, getAuthToken } = globalShared();
    const sum = (key: string) => props.stickers.reduce((t, s) => t + (Number(s[key]) || 0), 0);
    const number = (n: number) => new Intl.NumberFormat('en-US').format(n);
    const currency = (n: number) => new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(n);

    // All-time gross revenue of the org (every sale, not only stickers). Null = not loaded.
    const totalRevenue = ref<number | null>(null);

    async function loadTotalRevenue() {
      totalRevenue.value = null;
      if (!props.orgId) return;
      const today = dayjs().format('YYYY-MM-DD');
      try {
        const response = await makeGraphRequest(
          'query',
          findOrganizationTotalRevenue,
          { organizationId: props.orgId, startDate: today, endDate: today },
          getAuthToken(),
        );
        if (response?.errors?.length) throw new Error(response.errors[0].message);
        const cumulative = response?.data?.findOrganizationMetrics?.lastGrossRevenue?.cumulative;
        totalRevenue.value = Number(cumulative) || 0;
      } catch (e) {
        console.error('findOrganizationMetrics (total revenue):', e);
      }
    }

    watch(() => props.orgId, loadTotalRevenue, { immediate: true });

    const cards = computed(() => [
      {
        label: 'Total Impressions', value: number(sum('viewCount')), icon: 'sparkles', tone: 'is-indigo',
      },
      {
        label: 'Total Collected', value: number(sum('unitsSold')), icon: 'users', tone: 'is-teal',
      },
      {
        label: 'Total Shares', value: number(sum('shareCount')), icon: 'share-nodes', tone: 'is-pink',
      },
      {
        label: 'Total Donations', value: currency(sum('netRaised')), icon: 'heart', tone: 'is-yellow',
      },
      {
        label: 'Total Revenue',
        value: totalRevenue.value == null ? '—' : currency(totalRevenue.value),
        icon: 'dollar-sign',
        tone: 'is-blue',
      },
    ]);
    return { cards };
  },
});
</script>

<style lang="scss" scoped>
.c-sticker-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  :deep(svg) { width: 1em; height: 1em; }

  &__card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
  }
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    font-size: 18px;
    &.is-indigo { background: #e8e9fd; color: #4349dd; }
    &.is-pink { background: #ffe0ee; color: #ff6b9d; }
    &.is-yellow { background: #fef7e0; color: #8a7800; }
    &.is-teal { background: #c3eae3; color: #005c6b; }
    &.is-blue { background: #dde9f9; color: #4876c9; }
  }
  &__label {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #6b7280;
  }
  &__value { font-size: 24px; font-weight: 700; color: #111827; }
}
</style>
